import { translate } from "@/i18n/i18n";
import { donationFormatter, formatTime, orIfy } from "@/lib/utils";
import { InProgressTrainType } from "@ailenax-overlay/shared";
import { FC, useCallback, useEffect, useMemo, useState } from "react";

interface InProgressProps {
    train: InProgressTrainType;
    offset: number;
}

export const InProgress: FC<InProgressProps> = ({ train, offset }) => {
    const [timeRemaining, setTimeRemaining] = useState<number>(0);

    const updateTime = useCallback(() => {
        const now = new Date();
        const totalTime = train.expiresAt.getTime() - train.startedAt.getTime();
        const timeElapsed = now.getTime() - train.startedAt.getTime() - offset;
        setTimeRemaining(Math.max(0, Math.trunc((totalTime - timeElapsed) / 1000)));
    }, [train, offset]);

    useEffect(() => {
        updateTime();
        const interval = setInterval(updateTime, 1000);

        return () => clearInterval(interval);
    }, [updateTime]);

    const donationsToNextLevel = useMemo(() => {
        return donationFormatter.format(Math.ceil((train.required - train.progress) * 100) / 100);
    }, [train.progress, train.required]);

    const bitsToNextLevel = useMemo(() => {
        const bits = Math.ceil((train.required - train.progress) * 100);
        return `${bits.toLocaleString()} ${bits > 1 ? "Bits" : "Bit"}`;
    }, [train.progress, train.required]);

    const subsToNextLevel = useMemo(() => {
        const subs = Math.ceil((train.required - train.progress) / 1.6);
        return `${subs} ${subs > 1 ? "Subs" : "Sub"}`;
    }, [train.progress, train.required]);

    const requirements = useMemo(() => {
        return orIfy([donationsToNextLevel, subsToNextLevel, bitsToNextLevel]);
    }, [donationsToNextLevel, bitsToNextLevel, subsToNextLevel]);

    const percent = useMemo(() => {
        return Math.max(0, Math.trunc((train.progress / train.required) * 100));
    }, [train]);

    return (
        <div className="h-[100%] w-[100%] flex flex-row items-stretch justify-between relative">
            <div className={`absolute h-[300%] w-[${percent}%] left-0 top-0 transition-[width] duration-300 ease-in-out progress-bar`} style={{ width: `${Math.min(100, percent)}%`}}/>
            { train.record && train.record.level === train.level && train.record.percent > percent && (<div className="record-bar" style={{ left: `calc(${train.record.percent}% - 2px)`}}/>) }
            <div className="z-1 w-75 pl-3 flex-auto flex flex-col items-stretch justify-center gap-1">
                <div className="text-xl flex flex-row gap-2">
                    <div className="pr-3 train-level">Level {train.level}</div>
                    <div className="font-semibold">
                        <img src="https://static-cdn.jtvnw.net/emoticons/v2/emotesv2_19b1cfbfc4a749eca6ed63ce21112215/default/dark/3.0" style={{ display: "inline", position: "relative", top: "-2px", height: "2.2rem" }}/>
                        <img src="https://static-cdn.jtvnw.net/emoticons/v2/emotesv2_032e2c9c44604e8b82e5ee1bf3cb496c/default/dark/3.0" style={{ display: "inline", position: "relative", top: "-2px", height: "2.2rem" }}/>
                        <img src="https://static-cdn.jtvnw.net/emoticons/v2/emotesv2_14e53a08453544f2bb346b3b91ea29e8/default/dark/3.0" style={{ display: "inline", position: "relative", top: "-2px", height: "2.2rem" }}/>
                        { translate("DONO_TRAIN_IN_PROGRESS")}
                    </div>
                </div>
                { (train.required - train.progress) > 0 && (
                    <div className="text-base">
                        <div className="text-base">{ translate("DONO_TRAIN_AMOUNT_UNTIL_NEXT_LEVEL_PRE") } { requirements }</div>
                    </div>
                )}
            </div>
            <div className="z-1 w-25 pr-3 flex flex-col items-stretch justify-center gap-0">
                <div className="text-end self-end text-2xl time-remaining">
                    { formatTime(timeRemaining) }
                </div>
                <div className="text-end self-end text-4xl">
                    { percent }%
                </div>
            </div>
        </div>
    );
}